import { clone, sortBy, prop, pick, equals, isNil } from 'ramda'
import { defineAsyncComponent } from 'vue'

import {
  ENDPOINTS,
  MODALS,
  APPOINTMENT_MODES,
  BOOKING_TYPE_RESERVATION,
  FORMAT,
  APPOINTMENT_TABS,
  CUSTOMER_MODES,
  RECEIPT_STATUSES,
  LOCAL_STORAGE_KEYS,
  FEATURE_FLAGS,
} from '@/lib/consts.js'
import { ENTRANCE_PATH } from '@/lib/analytics/consts.js'

import { appointmentCalendarEvent, modalOpen } from '@/plugins/event-bus.js'

import {
  shouldAskAboutUpdateFuture,
  subbookingValidation,
  adjustStart,
  mapBookingCustomer,
  createSubbookingObject,
  createReservationObj,
  customerHasContact,
  customerHasEmail,
  customerHasCellPhone,
  convertReservationToAppointment,
  convertAppointmentToReservationObj,
  cardId,
  generateCustomerCard,
  appointmentChangeEvent,
  resourcesInSubbooking,
  metaDataOfBooking,
  getComboChildrenByIdOrIndex,
  adjustComboChildrenHours,
  hasStafferOrResourceAnyError,
  mapRequestAppointment,

} from '@/components/appointment/functions.js'
import { getPaymentsBasket } from '@/api/index.js'

import { APPOINTMENT_EVENTS, ERRORS } from '@/components/appointment/consts.js'
import { RECEIPT_STATUS_TYPE, PAYMENT_TYPE, BASKET_STATUS, BASKET_TYPE, BASKET_PAYMENT_TYPE_SOURCE } from '@/components/sales/checkout/consts.js'

import { useEppo } from '@/composables/eppo'
import { useLogger } from '@booksy-com/vue-tools'
import { useModal } from '@/composables/modal'
import moment from 'moment'
import { REPEATING_UPDATE_FUTURE_ACTION } from '@/components/appointment/repeating/consts.js'

import { useCalendarAnalyticsEvents } from '@/components/calendar/composables/calendar-analytics-events/index.js'

const logger = useLogger('store.appointment')

const DEFAULT_STATE = {
  appointment: null,
  appointmentPaymentDetails: null,
  model: null,
  customer: null,
  drawer: {
    subbookingIndex: null,
    editing: false,
    copy: null,
    deliberatelyBookedTill: false,
    comboHoursModalShowed: false,
  },
  hasErrors: {
    [APPOINTMENT_TABS.TRAVELING]: false,
  },
}

const MAX_LOOP_COUNTS = 5
const BOOKING_MODE_MODAL_MAX = 5

export default {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: {
    ...clone(DEFAULT_STATE),
  },

  /*!
   *
   * actions
   *
   */
  actions: {

    /*
     *
     * @public
     *
     */
    resetState ({ commit }) {
      logger.debug('resetState')

      commit('setup', clone(DEFAULT_STATE))
    },

    /*
     *
     * create empty appointment model
     * @public
     * appointment
     *
     */
    create ({ state, rootState, commit, dispatch, rootGetters }, { deliberatelyBookedTill, ...appointment }) {
      const customer = appointment.customer || {
        mode: null,
      }

      const data = {
        appointment: {
          customer,
          subbookings: [],
          business_secret_note: appointment.business_secret_note || '',
          business_note: appointment.business_note || '',
        },
      }

      const subbookings = appointment.subbookings && appointment.subbookings.length
        ? clone(appointment.subbookings)
        : [{}]

      subbookings
        .forEach((sb, index) => {
          let {
            day,
            booked_from,
            booked_till,
            appliance,
            service,
            service_variant,
            combo_children,
            ...restSb
          } = sb

          const staffer = rootGetters['business/staffers'].length === 1
            ? rootGetters['business/staffers'][0]
            : sb.staffer && sb.staffer.active !== false
              ? sb.staffer
              : undefined

          const serviceData = { service, service_variant }

          const subbooking = createSubbookingObject(
            { day, booked_from, booked_till, staffer, appliance, serviceData, combo_children, ...restSb },
            rootState.business.business.open_hours,
            rootState.app.currentTime
          )

          if (index > 0) {
            const duration = service?.variant?.duration
              ? service.variant.duration
              : moment.duration(moment(sb.booked_till) - moment(sb.booked_from)).asMinutes()
            subbooking.booked_from = null
            subbooking.booked_till = null
            subbooking.duration = duration
          }

          data.appointment.subbookings.push(subbooking)
        })

      return dispatch('saveCall', {
        appointment: data.appointment,
        dryRun: true,
      }).then(({ data }) => {
        const appointment = data.appointment
        const model = clone(data.appointment)
        const customer = !state.customer
          ? data.customer
          : data.customer && state.customer.merged_data.id !== data.customer.merged_data.id
            ? data.customer
            : state.customer
        commit('setup', {
          appointment,
          model,
          customer,
          subbookingIndex: model.subbookings.length > 1 ? null : 0,
          editing: true,
          deliberatelyBookedTill,
        })
        return appointment
      })
    },

    redo (
      { dispatch, rootState },
      { uid, customer, withCreate = true }
    ) {
      const businessId = rootState.business.business.id

      return dispatch(
        'api/call',
        {
          url: ENDPOINTS.APPOINTMENT_REDO,
          urlParams: {
            businessId,
            appointmentUid: uid,
          },
        },
        { root: true }
      ).then(({ data: { appointment } }) => {
        const currentTime = rootState.app.currentTime
        const from = moment(appointment.subbookings[0]?.booked_from)
        appointment.subbookings.forEach((sb, index) => {
          const duration = sb.service?.variant?.duration && !sb.addons?.length
            ? sb.service.variant.duration
            : moment.duration(moment(sb.booked_till) - moment(sb.booked_from)).asMinutes()
          const startDelay = moment.duration(index === 0 ? 0 : moment(sb.booked_from) - from).asMinutes()
          sb.booked_from = adjustStart(currentTime.clone().add(startDelay, 'minutes'))
          const bookedTill = sb.booked_from.clone().add(duration, 'minutes')
          sb.booked_till = bookedTill.isAfter(currentTime, 'day')
            ? currentTime.clone().set({ hour: 23, minute: 55, second: 0, millisecond: 0 })
            : bookedTill

          if (!sb.staffer_id) {
            sb.staffer_id = undefined
            sb.staffer = undefined
          }
          if (!sb.appliance_id) {
            sb.appliance_id = undefined
            sb.appliance = undefined
          }
        })
        if (customer) {
          appointment.customer = generateCustomerCard(customer)
        }
        if (withCreate) {
          return dispatch('create', appointment)
        }
        return appointment
      })
    },

    /*
     *
     * add new subboking
     * @public
     *
     */
    addSubbooking ({ state, dispatch, commit, rootState }, { from, till, staffer, appliance, serviceData }) {
      const subbooking = createSubbookingObject(
        {
          booked_from: from,
          booked_till: till,
          staffer,
          appliance,
          serviceData,
          combo_children: [],
        },
        rootState.business.business.open_hours
      )

      const appointment = clone(state.model)

      appointment.subbookings.push(subbooking)

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
      }).then(({ data }) => {
        const sbLastIndex = data.appointment.subbookings.length - 1
        data.appointment.subbookings[sbLastIndex].isNewSubbooking = true
        commit('setModel', data.appointment)
      }).catch(e => {
        commit('setModel', appointment)
        return Promise.reject(e)
      })
    },

    /*
     *
     * load appointment
     * @public
     *
     */
    load ({ state, commit, dispatch, getters, rootState }, { id, edit = false, type, reservation = false }) {
      logger.debug('load appointment', { id, edit })
      const action = reservation ? 'reservation' : 'appointmentLoad'

      return new Promise(async (resolve, reject) => {
        // if edit mode and drawer is open then show discard modal
        if (edit && getters.isEdited) {
          await modalOpen(MODALS.APPOINTMENT_DISCARD)
        }

        if (edit) {
          dispatch('loadingScreen/show', null, { root: true })
        }

        dispatch(`api/${action}`, { id, type }, { root: true })
          .then(async ({ appointment, customer, physioVisitRedirect }) => {
            let model = edit ? clone(appointment) : null
            if (model) model.physio_visit_redirect = physioVisitRedirect
            let _customer = clone(customer)

            if (!_customer && appointment.customer && appointment.customer.mode === CUSTOMER_MODES.MANUAL) {
              _customer = {
                merged_data: {
                  full_name: appointment.customer.name,
                },
              }
            }

            const subbookingIndex = edit && model.subbookings.length === 1 ? 0 : null

            let appointmentPaymentDetails = null
            const eppo = useEppo()
            const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
            if (isPaymentDetailsFromBasketEnabled && appointment.basket_id) {
              try {
                const { data: { result } } = await getPaymentsBasket({
                  businessId: rootState.business.business.id,
                  basketId: appointment.basket_id,
                })
                appointmentPaymentDetails = result
              } catch (error) {
                logger.error('Failed to fetch payment basket', error)
              }
            }

            commit('setup', {
              appointment,
              appointmentPaymentDetails,
              model,
              customer: _customer,
              subbookingIndex,
            })

            // Resolve this promise
            resolve(true)
          })
          .catch(reject)
          .finally(() => {
            if (edit) {
              dispatch('loadingScreen/hide', null, { root: true })
            }
          })
      })
    },

    /*
     *
     * called from calendar, when card is dropped
     * @public
     *
     */
    async cardDrop ({ state, getters, dispatch, commit }, bookingData) {
      const {
        id,
        booked_from,
        booked_till,
        staffer,
        appliance,
        index,
        changeDay,
        ...restData
      } = bookingData

      logger.debug('cardDrop', { id, booked_from, booked_till, staffer, appliance, index, changeDay, restData })

      // shortcut: edit mode and reservation type
      if (state.model && state.model.type === BOOKING_TYPE_RESERVATION) {
        const data = {}
        if (booked_from) data.booked_from = booked_from
        if (booked_till) data.booked_till = booked_till

        if (staffer) {
          data.staffer = staffer
          data.staffer_id = staffer.id
        }

        if (appliance) {
          data.appliance = appliance
          data.appliance_id = appliance.id
        }

        return commit('updateReservationData', data)
      }

      if (state.model) {
        // let updatedBooking
        if (restData.comboChildren) {
          const modelBooking = restData.parentId
            ? clone(getters.getSubbookingById(restData.parentId))
            : clone(getters.getSubbookingByIndex(restData.parentIndex))

          const comboBooking = getComboChildrenByIdOrIndex(
            modelBooking,
            { id: restData.ccId, index }
          )

          const updatedCombo = await dispatch('updateModelSubbooking', {
            booking: comboBooking,
            values: bookingData,
            comboSubbooking: true,
          })

          modelBooking.combo_children[index] = updatedCombo

          const { bookedFromDiff, bookedTillDiff } = restData

          if (bookedFromDiff || bookedTillDiff) {
            await dispatch('handleComboHoursEditModal')
          }

          if (bookedFromDiff || bookedTillDiff) {
            modelBooking.combo_children = adjustComboChildrenHours(
              clone(modelBooking.combo_children),
              index,
              { bookedFromDiff, bookedTillDiff },
              modelBooking.service.combo_type
            )
          }

          return dispatch('updateSubbooking', {
            booking: modelBooking,
            index: restData.parentIndex,
            nullifySubsequent: true,
            changeDay,
          })
        } else {
          const modelBooking = id
            ? clone(getters.getSubbookingById(id))
            : clone(getters.getSubbookingByIndex(index))

          const updatedBooking = await dispatch('updateModelSubbooking', {
            booking: modelBooking,
            values: bookingData,
          })

          return dispatch('updateSubbooking', { booking: updatedBooking, index, nullifySubsequent: true, changeDay })
        }
      }

      const data = {
        _version: restData.booking._version,
        id,
        booked_from,
        booked_till,
        staffer_id: staffer && staffer.id,
        appliance_id: appliance && appliance.id,
        combo_parent_id: restData.booking.combo_parent_id,
      }

      // simple drag & drop
      return dispatch('cardDropSave', { booking: data, resource: staffer || appliance, service: restData.booking.service })
    },

    /*
     *
     * save booking after calendar card drag
     * @protected
     *
     */
    async cardDropSave ({ state, dispatch, rootState }, { booking, resource, service }) {
      logger.debug('cardDropSave', booking)

      const { notifyAboutReschedule, updateFutureBookings } = await dispatch('confirmModals', { model: state.appointment, booking })

      return dispatch('cardDropCall', {
        booking,
        notifyAboutReschedule,
        updateFutureBookings,
        resource,
        service,
      })
    },

    /*
     *
     * card drop api call
     * @protected
     * @property Object   booking - droped booking
     * @property Boolean  overbooking - true if you want to overbook selected time slot
     * @property Boolean  notifyAboutReschedule - true if in modal user selects to notify about change
     * @property Boolean  updateFutureBookings - true only if repeating and if in modal user selects to update all future bookings
     * @property Boolean  updateFutureBookings - true if user wants to assign booking with service on staffer that dont execute this service
     * @property Objects  staffer/appliance/service - additional objects needed for modal content
     *
     */
    cardDropCall ({ state, rootState, dispatch }, {
      booking,
      overbooking = false,
      notifyAboutReschedule = false,
      updateFutureBookings = undefined,
      forceNoVariantService = false,
      resource,
      service,
    }) {
      logger.debug('cardDropCall', {
        booking,
        overbooking,
        notifyAboutReschedule,
        updateFutureBookings,
      })

      const businessId = rootState.business.business.id

      dispatch('loadingScreen/show', null, { root: true })

      return dispatch(
        'api/call',
        {
          url: ENDPOINTS.APPOINTMENT_DRAG,
          urlParams: {
            businessId,
            bookingId: booking.id,
          },
          method: 'put',
          data: {
            ...booking,
            overbooking: overbooking,
            _notify_about_reschedule: notifyAboutReschedule,
            _update_future_bookings: updateFutureBookings,
            _force_no_variant_service: forceNoVariantService,
            _update_combo_bookings: !!booking.combo_parent_id,
          },
        },
        { root: true }
      )
        .then((response) => {
          useCalendarAnalyticsEvents()?.sendEventAboutCardEventChangedByDragging?.({
            orginalAppointment: state?.appointment,
            editedBooking: booking,
          })
          return response?.data
        })
        .catch((error) => {
          dispatch('loadingScreen/hide', null, { root: true })
          // conflict with others appointment
          if (error.request.status === 409) {
            return modalOpen(MODALS.APPOINTMENT_OVERBOOKING, {
              error: error.response,
            }).then(overbooking => {
              return dispatch('cardDropCall', {
                booking,
                overbooking,
                notifyAboutReschedule,
                updateFutureBookings,
                forceNoVariantService,
              })
            }).catch(e => {
              return Promise.reject(e)
            })
          } else if (error.request.status === 400) {
            logger.debug('drag error', error.response)
            const { response } = error
            if (response.data && response.data.errors) {
              const [errorDetails] = response.data.errors
              if (errorDetails.code === ERRORS.DRAG_NOT_ALLOWED) {
                const _error = { ...error, dragNotAllowed: true }
                return Promise.reject(_error)
              }
            }
            return modalOpen(MODALS.APPOINTMENT_CHANGE_RESOURCE, {
              resource,
              service,
            }).then(forceNoVariantService => {
              return dispatch('cardDropCall', {
                booking,
                notifyAboutReschedule,
                updateFutureBookings,
                forceNoVariantService,
              })
            }).catch(e => {
              return Promise.reject(e)
            })
          } else {
            return Promise.reject(error)
          }
        }).finally(() => {
          dispatch('loadingScreen/hide', null, { root: true })
        })
    },

    /*
     *
     * update (sub-)booking
     * @public
     * @property Boolean    preserveOrder - save front order of bookings
     *
     */
    async updateSubbooking (
      { state, getters, dispatch, commit },
      { booking, index, preserveOrder, changeDay, comboHoursEdit }
    ) {
      logger.debug('updateSubbooking', index, booking)

      let changeIndex = state.drawer.subbookingIndex !== null

      logger.debug('updateSubbooking', changeIndex)

      const appointment = clone(state.model)
      appointment.subbookings[index] = booking

      if (comboHoursEdit) {
        await dispatch('handleComboHoursEditModal')
      }

      if (getters.isMultibooking && !changeDay) {
        // check if reorder is needed - bookedFrom might have changed
        appointment.subbookings[index]._thisIsTheOne = true // mark it
        appointment.subbookings = sortBy(prop('booked_from'), appointment.subbookings)
        index = appointment.subbookings.findIndex(subbooking => subbooking._thisIsTheOne)
        delete appointment.subbookings[index]._thisIsTheOne // unmark it
      }

      const hasComboChildren = appointment.subbookings.some(s => !!s.combo_children?.length)

      if ((getters.isMultibooking || hasComboChildren) && changeDay) {
        const date = changeDay
        const getBookedFrom = (subbooking) => moment(subbooking.booked_from).year(date.year()).month(date.month()).date(date.date()).format(FORMAT)
        const getBookedTill = (subbooking) => moment(subbooking.booked_till).year(date.year()).month(date.month()).date(date.date()).format(FORMAT)

        appointment.booked_from = getBookedFrom(appointment)
        appointment.booked_till = getBookedTill(appointment)

        for (let [i, subbooking] of appointment.subbookings.entries()) {
          logger.debug('change dayf of ', i, '. subboking')
          if (subbooking.combo_children?.length) {
            for (const cc of subbooking.combo_children) {
              cc.booked_from = getBookedFrom(cc)
              cc.booked_till = getBookedTill(cc)
            }
          }

          subbooking.booked_from = getBookedFrom(subbooking)
          subbooking.booked_till = getBookedTill(subbooking)
        }
      }

      if (booking.service) {
        const staffersPerformService = [
          ...(booking.service.staffer_ids || []),
          ...(booking.service.variant?.staffers || []),
        ]
        const appliancesPerformService = [
          ...(booking.service.appliance_ids || []),
          ...(booking.service.resources || []),
        ]

        const stafferInvalid = booking.staffer_id
          && booking.staffer_id !== -1
          && staffersPerformService.length
          && !staffersPerformService.includes(booking.staffer_id)
        const applianceInvalid = booking.appliance_id
          && booking.appliance_id !== -1
          && appliancesPerformService.length
          && !appliancesPerformService.includes(booking.appliance_id)

        // TODO - improve code quality
        const comboStafferValid = () => {
          if (booking.combo_children) {
            return booking.combo_children?.every(combo => {
              const { staffer_id, service } = combo
              if (staffer_id === -1) return true
              return service.staffer_ids.includes(staffer_id)
            })
          } else {
            return true
          }
        }

        if ((stafferInvalid || applianceInvalid || !comboStafferValid())) {
          const change = await modalOpen(MODALS.APPOINTMENT_CHANGE_RESOURCE, {
            service: booking.service,
            resource: (stafferInvalid && booking.staffer)
              || (applianceInvalid && booking.appliance),
          })

          // allow to add combo serivce with variant if it's not performed by this Staffer
          if (change && !booking.service.combo_type) {
            booking.service = {
              name: booking.service.name,
              variant: {},
            }
            booking.service_variant = {
              mode: 'no-variant',
              service_name: booking.service.name,
            }
          }
        }
      }

      const _preserveOrder = preserveOrder !== undefined ? preserveOrder : state.drawer.subbookingIndex !== null

      logger.debug('preserveOrder', _preserveOrder)

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
        preserveOrder: _preserveOrder,
      }).then(({ data }) => {
        if (booking.isNewSubbooking) {
          data.appointment.subbookings[index].isNewSubbooking = true
        }
        if (changeIndex) {
          commit('setDrawerEditedIndex', { index, copy: false })
        }
        commit('setModel', data.appointment)
        appointmentChangeEvent(data.appointment?.subbookings[index])
      }).catch(({ response }) => {
        if (changeIndex) {
          commit('setDrawerEditedIndex', { index, copy: false })
        }
        commit('setModel', appointment)
        // partial data still not ok - update it with model anyway
        // no need for post-dry_run stuff - we use unmodified model
        // we will show errors from API
        return Promise.reject(response)
      })
    },

    /*
     *
     * change order of subbookings by drag&drop on appointment drawer
     *
     */
    changeSubbokingsOrder ({ state, dispatch, commit }, { from, to }) {
      logger.info('[appointment] dragDrop', from, to)

      // dry_run on appointment with changed subbooking order
      const appointment = clone(state.model)

      // move dragged to new position bookings
      const dragged = appointment.subbookings.splice(from, 1)[0]
      appointment.subbookings.splice(to, 0, dragged)

      if (to === 0 || from === 0) {
        // if dragged booking was first/or is first now, copy booking time from it
        const firstElementToCopy = to === 0 ? appointment.subbookings[1] : dragged

        const firstElement = appointment.subbookings[0]

        // calculate duration to preserve it
        const toDuration = moment.duration(
          moment(firstElement.booked_till)
          - moment(firstElement.booked_from)
        )

        firstElement.booked_from = firstElementToCopy.booked_from
        firstElement.booked_till = moment(firstElementToCopy.booked_from)
          .add(toDuration).format(FORMAT)
        if (firstElement?.combo_children.length) {
          firstElement.combo_children = firstElement.combo_children.map((cc, index) => {
            if (index > 0) {
              cc.booked_from = null
              cc.booked_till = null
            } else {
              cc.booked_from = firstElementToCopy.booked_from
              cc.booked_till = null
            }
            return cc
          })
        }
      }

      appointment.booked_till = null

      // nullify booking dates for rest of the subbookings
      for (const [index, subbooking] of appointment.subbookings.entries()) {
        if (index >= Math.min(from, to) && index !== 0) {
          const duration = moment.duration(
            moment(subbooking.booked_till)
            - moment(subbooking.booked_from)
          )
          subbooking.booked_from = null
          subbooking.booked_till = null
          subbooking.duration = duration.asMinutes() // preserve duration

          if (subbooking?.combo_children?.length) {
            subbooking.combo_children = subbooking.combo_children.map(cc => {
              cc.booked_from = null
              cc.booked_till = null
              cc.duration = duration.asMinutes()
              return cc
            })
          }
        }
      }

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
        preserveOrder: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch(({ response }) => {
        return Promise.reject(response)
      })
    },

    /*
     *
     * delete (sub-)booking
     * @public
     *
     */
    deleteSubbooking ({ state, dispatch, commit }, index) {
      const appointment = clone(state.model)

      appointment.subbookings.splice(index, 1)

      logger.debug('deleteSubbooking', index)

      const subbookingIndex = appointment.subbookings.length === 1 ? 0 : null

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
        commit('setDrawerEditedIndex', { index: subbookingIndex })
      })
    },

    /*
     *
     * update customer
     * @public
     *
     */
    updateCustomer ({ state, dispatch, commit }, customerCard) {
      logger.debug('updateCustomer', customerCard)

      const appointment = clone(state.model)

      appointment.customer = customerCard

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
        preserveOrder: true, // important - do NOT remove!
      })
        .then(({ data }) => {
          commit('setModel', data.appointment)
          if (data.customer) {
            commit('setCustomer', data.customer)
          } else if (data.appointment?.customer?.mode === CUSTOMER_MODES.MANUAL) {
            commit('setCustomer', {
              merged_data: {
                full_name: data.appointment.customer.name,
              },
            })
          } else {
            commit('setCustomer', null)
          }
        })
        .catch(({ response }) => {
          return Promise.reject(response)
        })
    },

    /*
     *
     * update travel prive
     * @public
     *
     */
    updateTravelPrice ({ state, dispatch, commit }, travelPrice) {
      const appointment = clone(state.model)

      appointment.traveling.price = travelPrice

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
        preserveOrder: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch(({ response }) => {
        return Promise.reject(response)
      })
    },

    async updateModelSubbooking (
      { state, getters, dispatch, commit },
      { booking: bookingData, values, comboSubbooking }
    ) {
      const {
        booked_from,
        booked_till,
        staffer,
        appliance,
      } = values

      let booking = bookingData

      if (booked_from) booking.booked_from = booked_from
      if (booked_till) booking.booked_till = booked_till

      const service = bookingData.service
      const previousStaffer = bookingData.staffer
      const stafferChange = !!staffer
      const resourceProp = stafferChange ? 'staffer_ids' : 'appliance_ids'
      let resource

      if (staffer) {
        booking.staffer = staffer
        booking.staffer_id = staffer.id
        if (stafferChange && staffer.id !== previousStaffer?.id) {
          booking.is_staffer_requested_by_client = false
          booking.autoassign = false
        }
        resource = staffer
      }

      if (appliance) {
        booking.appliance = appliance
        booking.appliance_id = appliance.id
        resource = appliance
      }

      if (!comboSubbooking && resource && service && service[resourceProp] && !service[resourceProp].includes(resource.id)) {
        const change = await modalOpen(MODALS.APPOINTMENT_CHANGE_RESOURCE, {
          service,
          resource,
        })

        if (change) {
          booking.service = {
            name: service.name,
            variant: {},
          }
          booking.service_variant = {
            mode: 'no-variant',
            service_name: service.name,
          }
        }
      }

      // shortcut: edit mode
      return booking
    },

    /*
     *
     * dryRun and update all state
     * @public
     *
     */
    dryUpdateState ({ state, dispatch, commit }) {
      const appointment = clone(state.model)

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
        preserveOrder: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch(({ response }) => {
        return Promise.reject(response)
      })
    },

    /*
     *
     * update repeating
     *
     */
    async updateRepeating ({ state, dispatch, commit }, { repeating }) {
      logger.debug('updateRepeating', repeating)

      // appointment (model)
      const appointment = clone(state.model)

      const firstBooking = appointment.subbookings[0]

      // update booking hours
      if (repeating && repeating.booking_dates && repeating.booking_dates.length) {
        firstBooking.booked_from = repeating.booking_dates[0].booked_from
        firstBooking.booked_till = repeating.booking_dates[0].booked_till
      }

      appointment.new_repeating = repeating

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch(({ response }) => {
        return Promise.reject(response)
      })
    },

    async updateDeposit ({ state, dispatch, commit }, { deposit }) {
      logger.debug('updateDeposit', deposit)

      // appointment (model)
      const appointment = clone(state.model)

      appointment.deposit = deposit

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch((error) => {
        return Promise.reject(error)
      })
    },

    async updateDepositWithExternalPayment ({ state, dispatch, commit }, { deposit, externalPaymentMethod, isOverbooking }) {
      logger.debug('updateDepositWithExternalPayment', { deposit, externalPaymentMethod })

      const appointment = clone(state.model)

      appointment.deposit = deposit
      appointment.external_payment_method = externalPaymentMethod

      return dispatch('saveCall', {
        appointment,
        overbooking: isOverbooking,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      }).catch(({ response }) => {
        return Promise.reject(response)
      })
    },

    async getAppointmentData ({ dispatch, commit, rootState }, { id }) {
      logger.debug('getAppointmentData', id)

      try {
        const { data } = await dispatch('api/call', {
          url: ENDPOINTS.APPOINTMENT_UID,
          urlParams: {
            businessId: rootState.business.business.id,
            appointmentUid: id,
          },
        }, { root: true })

        commit('setModel', data.appointment)
        commit('setup', {
          appointment: data.appointment,
          model: data.appointment,
          customer: data.customer,
          subbookingIndex: data.appointment.subbookings.length === 1 ? 0 : null,
        })
        return data.appointment
      } catch (error) {
        logger.error('Failed to get appointment data:', error)
        return Promise.reject(error)
      }
    },

    /*
     * Notify customer that merchant is ready for the appointmet
     */
    async readyNotifyAction ({ state, dispatch, rootState }) {
      dispatch('loadingScreen/show', null, { root: true })
      return dispatch('api/call',
        {
          method: 'POST',
          url: ENDPOINTS.APPOINTMENT_READY,
          urlParams: {
            businessId: rootState.business.business.id,
            appointmentUid: state.model.appointment_uid,
          },
        },
        { root: true }
      ).finally(dispatch('loadingScreen/hide', null, { root: true }))
    },

    /*
     *
     * save action on appointment
     * @public
     *
     */
    async saveAction (
      { state, dispatch, getters, rootState, rootGetters, commit },
      { action, forceUpdateFutureBookings }
    ) {
      logger.debug('save action', action)

      const appointment = clone(state.model)

      const { deposit_info, auto_release_deposit_on_cancel } = appointment.payment_info
      let canWaiveCancellationFee = !auto_release_deposit_on_cancel
        && deposit_info?.short_status !== RECEIPT_STATUS_TYPE.CANCELED
        && deposit_info?.short_status !== RECEIPT_STATUS_TYPE.SUCCESS
        && !isNil(deposit_info?.id)

      // TODO HAM-3280
      const eppo = useEppo()
      const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
      if (isPaymentDetailsFromBasketEnabled) {
        canWaiveCancellationFee = !state.appointmentPaymentDetails?.auto_release_deposit_on_cancel
          && ![BASKET_STATUS.CANCELED, BASKET_STATUS.SUCCESS].includes(state.appointmentPaymentDetails?.payments_summary?.status?.code)
          && !isNil(state.appointmentPaymentDetails?.id)
      }

      const url = ENDPOINTS.APPOINTMENT_ACTION

      // current business identifier
      const businessId = rootState.business.business.id

      const urlParams = {
        businessId,
        appointmentUid: appointment.appointment_uid,
      }

      // request data
      const data = {
        action: action,
        _version: appointment._version,
      }

      if (action === 'cancel-repeating') {
        data.action = 'cancel'
      }

      if (action === 'cancel') {
        const { business_note, notifyByEmail, notifyBySms } = await modalOpen(MODALS.APPOINTMENT_CANCEL)
        data._notify_about_cancel_email = notifyByEmail
        data._notify_about_cancel_sms = notifyBySms
        data.business_note = business_note
      }

      if (canWaiveCancellationFee && action !== 'confirm') {
        const { release_deposit } = await modalOpen(MODALS.APPOINTMENT_RELEASE_DEPOSIT, {
          total_deposit: state.appointmentPaymentDetails?.payments_summary?.total?.amount_formatted || deposit_info.total,
        })
        data.release_deposit = release_deposit
      }

      if (!forceUpdateFutureBookings && action === 'cancel') {
        const currentTime = rootState.app.currentTime ? new Date(rootState.app.currentTime) : Date.now()
        const askAboutFutureAppointments = shouldAskAboutUpdateFuture({
          editedAppointment: appointment,
          currentTime,
          originalAppointment: state.appointment,
          action: REPEATING_UPDATE_FUTURE_ACTION.CANCEL,
        })
        let updateFutureBookings
        logger.debug('ask about update future appointments', askAboutFutureAppointments)
        if (askAboutFutureAppointments) {
          updateFutureBookings = await modalOpen(MODALS.APPOINTMENT_UPDATE_FUTURE, { action: REPEATING_UPDATE_FUTURE_ACTION.CANCEL })
          data._update_future_bookings = updateFutureBookings
        }
      } else {
        data._update_future_bookings = true
      }

      if (action === 'no_show') {
        const { release_deposit, _notify_about_no_show } = await modalOpen(MODALS.APPOINTMENT_NO_SHOW)
        data.release_deposit = release_deposit
        data._notify_about_no_show = _notify_about_no_show
      }

      dispatch('loadingScreen/show', null, { root: true })

      // make request
      return dispatch('api/call', {
        url,
        urlParams,
        method: 'POST',
        data,
      }, {
        root: true,
      }).then(async response => {
        let appointment = response.data.appointment

        const eppo = useEppo()

        const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
        let appointmentPaymentDetails = null
        if (isPaymentDetailsFromBasketEnabled) {
          if (appointment.basket_id) {
            try {
              const { data: { result } } = await getPaymentsBasket({
                businessId: rootState.business.business.id,
                basketId: appointment.basket_id,
              })
              appointmentPaymentDetails = result
            } catch (error) {
              logger.error('Failed to fetch payment basket', error)
            }
          }
        }

        const shouldShowBookingModeModal = _shouldShowBookingModeModal(appointment, appointmentPaymentDetails, rootGetters, eppo, getters)

        if (!isPaymentDetailsFromBasketEnabled) {
          const prepaymentStatus = appointment.payment_info?.transaction_info?.status_type
          const depositStatus = appointment.payment_info?.deposit_info?.status_type
          if ([prepaymentStatus, depositStatus].includes(RECEIPT_STATUSES.AUTHORIZED)) {
            const id = response.data.appointment.appointment_uid
            let counter = 0
            try {
              appointment = (await dispatch('reloadAppointmentPaymentStatus', { id, counter })).appointment
            } catch (err) {
              // if this call throw an error, saving appointment in state should be proceed
            }
          }
        }

        const model = clone(appointment)
        const customer = response.data.customer.merged_data && clone(response.data.customer)
        const subbookingIndex = model.subbookings.length === 1 ? 0 : null

        commit('setup', {
          appointment,
          model,
          customer,
          subbookingIndex,
          appointmentPaymentDetails,
        })

        if (data.action === 'cancel' && data._update_future_bookings) {
          appointmentCalendarEvent(APPOINTMENT_EVENTS.REPEATING_DELETED)
        }

        appointmentCalendarEvent(APPOINTMENT_EVENTS.SAVE_ACTION_SUCCESS, { data, appointment })

        // show refund modal after appointment cancellation if it was paid with Booksy Pay
        const booksyPayPaymentRow = getters.getPaymentRowByPaymentType(BASKET_PAYMENT_TYPE_SOURCE.BOOKSY_PAY)
        const shouldShowRefundModal = _shouldShowRefundModal(booksyPayPaymentRow, rootGetters)

        if (data.action === 'cancel' && shouldShowRefundModal) {
          dispatch('loadingScreen/hide', null, { root: true })
          const eppo = useEppo()
          const isPartialRefundEnabled = eppo.ff(FEATURE_FLAGS.Feature_PartialRefund)
          if (isPartialRefundEnabled) {
            const paymentRow = getters.getPaymentRowByPaymentType(BASKET_PAYMENT_TYPE_SOURCE.BOOKSY_PAY)
            const modal = useModal()
            await new Promise((resolve, reject) => {
              modal.open({
                width: '448px',
                clickOutsideCloses: false,
                content: {
                  $component: {
                    is: defineAsyncComponent(() => import('@/components/app/modals/refund-payment.vue')),
                    refundRow: paymentRow,
                    entrancePath: ENTRANCE_PATH.BP_APPT_CANCELLATION,
                    '@reject': () => {
                      modal.cancel()
                      resolve()
                    },
                    '@resolve': () => {
                      modal.close()
                      resolve()
                    },
                  },
                },
              })
            })
          } else {
            const paymentRow = getters.getPaymentRowByPaymentType(BASKET_PAYMENT_TYPE_SOURCE.BOOKSY_PAY)
            const paymentRowId = paymentRow?.id
            const basketPaymentId = paymentRow?.basket_payment_id
            await modalOpen(MODALS.APPOINTMENT_CANCELED_REFUND, { paymentRowId, basketPaymentId })
          }
        }

        if (data.action === 'confirm' && shouldShowBookingModeModal) {
          dispatch('loadingScreen/hide', null, { root: true })
          await modalOpen(MODALS.APPOINTMENT_BOOKING_MODE)
        }
      }).finally(() => {
        dispatch('loadingScreen/hide', null, { root: true })
      })
    },

    /*
     *
     * reloadAppointmentPaymentStatus
     *
     */
    reloadAppointmentPaymentStatus ({ dispatch }, { id, counter }) {
      logger.debug('reloadAppointmentPaymentStatus')
      // New pos refactor will not show the receipt staus right after change
      // The receiept status in badge won't be refreshed
      // Thats why we are forced to ask api couple more times for the newest status
      return new Promise(async (resolve, reject) => {
        let getAppointmentTimeout
        try {
          const { appointment } = await dispatch('api/appointmentLoad', { id }, { root: true })
          const prepaymentStatus = appointment?.payment_info?.transaction_info?.status_type
          const depositStatus = appointment?.payment_info?.deposit_info?.status_type

          if (!(prepaymentStatus || depositStatus)) throw new Error('No payment status to check')

          if ([prepaymentStatus, depositStatus].includes(RECEIPT_STATUSES.AUTHORIZED)) {
            if (counter >= MAX_LOOP_COUNTS) return resolve({ appointment })

            getAppointmentTimeout = setTimeout(() => {
              counter += 1
              resolve(dispatch('reloadAppointmentPaymentStatus', { id, counter }))
            }, 1000)
          } else {
            resolve({ appointment })
          }
        } catch (error) {
          clearTimeout(getAppointmentTimeout)
          logger.error(error)
          reject(error)
        }
      })
    },

    /*
     *
     * save
     * @public
     *
     */
    async save ({ state, dispatch }) {
      logger.debug('save')

      // appointment (model)
      const appointment = clone(state.model)

      const { notifyAboutReschedule, updateFutureBookings } = await dispatch('confirmModals', { model: appointment })

      return dispatch('saveCall', { appointment, notifyAboutReschedule, updateFutureBookings }).catch((error) => {
        // conflict with others appointment
        if (error.request.status === 409) {
          return modalOpen(MODALS.APPOINTMENT_OVERBOOKING, {
            error: error.response,
          }).then(overbooking => {
            return dispatch('saveCall', {
              appointment,
              overbooking,
              notifyAboutReschedule,
              updateFutureBookings,
            })
          }).catch(e => {
            return Promise.reject(e)
          })
        } else {
          return Promise.reject(error.response)
        }
      })
    },

    /*
     *
     * save model api call
     * @protected
     *
     */
    saveCall ({ rootState, dispatch, rootGetters }, {
      appointment,
      dryRun = false,
      overbooking = false,
      notifyAboutReschedule = false,
      updateFutureBookings = undefined,
      preserveOrder = false,
      notificationEnabled = true,
    }) {
      const requestAppointment = mapRequestAppointment(appointment)

      logger.debug('saveCall', {
        appointment: requestAppointment,
        dryRun,
        overbooking,
        notifyAboutReschedule,
        updateFutureBookings,
        preserveOrder,
        notificationEnabled,
      })

      dispatch('loadingScreen/show', null, { root: true })
      return dispatch(
        'api/appointmentSave',
        {
          appointment: requestAppointment,
          dryRun,
          overbooking,
          notifyAboutReschedule,
          updateFutureBookings,
          preserveOrder,
          notificationEnabled,
        },
        { root: true }
      ).finally(() => {
        dispatch('loadingScreen/hide', null, { root: true })
      })
    },

    /*
     *
     * create reservation as appointment object
     *
     */
    createReservation ({ rootState, commit }, { day, booked_from, booked_till, staffer, appliance } = {}) {
      // create subboking obj with inital data
      const subbookingReservation = createReservationObj({ day, from: booked_from, till: booked_till, staffer, appliance }, rootState.business.business.open_hours, true)

      // convert reservation subbooking to appointment object
      const appointment = convertReservationToAppointment(subbookingReservation)
      const model = clone(appointment)

      commit('setup', {
        appointment,
        model,
      })

      return Promise.resolve()
    },

    /*
     *
     * save reservation api call
     * @public
     *
     */
    saveReservation ({ state, dispatch }) {
      const reservationModel = clone(state.model)
      const reservation = convertAppointmentToReservationObj(clone(reservationModel))

      dispatch('loadingScreen/show', null, { root: true })

      return dispatch('api/reservationSave', {
        reservation,
      }, {
        root: true,
      }).catch((error) => {
        dispatch('loadingScreen/hide', null, { root: true })
        // conflict with others appointment
        if (error.request.status === 409) {
          return modalOpen(MODALS.APPOINTMENT_OVERBOOKING, {
            error: error.response,
          }).then(overbooking => {
            return dispatch('api/reservationSave', {
              reservation,
              overbooking,
            }, {
              root: true,
            })
          }).catch(e => {
            return Promise.reject(e)
          })
        } else {
          return Promise.reject(error)
        }
      }).finally(() => {
        dispatch('loadingScreen/hide', null, { root: true })
      })
    },

    async deleteReservation ({ dispatch }, id) {
      await modalOpen(MODALS.APPOINTMENT_CANCEL)

      dispatch('loadingScreen/show', null, { root: true })

      return dispatch('api/call', {
        url: ENDPOINTS.RESERVATION,
        urlParams: {
          reservationId: id,
        },
        method: 'delete',
      }, {
        root: true,
      }).finally(() => {
        dispatch('loadingScreen/hide', null, { root: true })
      })
    },

    /*
     *
     * set edited index
     *
     */
    setDrawerEditedIndex ({ commit }, { index }) {
      commit('setDrawerEditedIndex', { index })
    },

    /*
     *
     * confirm appointment update, ash about future bookings etc.
     *
     */
    async confirmModals ({ rootState, state }, { model, booking }) {
      const originalAppointment = state.appointment
      const editedAppointment = {
        ...clone(model),
        ...booking ? {
          booked_from: booking.booked_from,
          booked_till: booking.booked_till,
        } : {},
      }
      const currentTime = rootState.app.currentTime ? new Date(rootState.app.currentTime) : Date.now()
      const willUpdateFutureBooking = new Date(originalAppointment.booked_till) > currentTime
      const confirmUpdate = editedAppointment.appointment_id
        ? await modalOpen(MODALS.APPOINTMENT_CONFIRM_UPDATE, {
          futureAppointment: willUpdateFutureBooking,
          booking,
        })
        : {}

      const askAboutFutureAppointments = shouldAskAboutUpdateFuture({
        originalAppointment: state.appointment,
        editedAppointment,
        currentTime,
        action: REPEATING_UPDATE_FUTURE_ACTION.UPDATE,
      })
      logger.debug('ask about update future appointments', askAboutFutureAppointments)
      const updateFutureBookings = askAboutFutureAppointments
        ? await modalOpen(MODALS.APPOINTMENT_UPDATE_FUTURE, { action: REPEATING_UPDATE_FUTURE_ACTION.UPDATE })
        : undefined

      return {
        notifyAboutReschedule: confirmUpdate.notify,
        updateFutureBookings,
      }
    },

    /*
     * update consent forms information
     */
    updateConsentForm ({ state, commit, dispatch }, { uuid, data }) {
      const appointment = clone(state.model)
      const { consent_forms: consentForms } = appointment
      const formIndex = consentForms.findIndex(c => c.consent_uuid === uuid)
      consentForms[formIndex] = {
        ...consentForms[formIndex],
        ...data,
      }

      appointment.consent_forms = consentForms

      return dispatch('saveCall', {
        appointment,
        dryRun: true,
      }).then(({ data }) => {
        commit('setModel', data.appointment)
      })
    },

    async handleComboHoursEditModal (
      { state, commit }
    ) {
      if (state.drawer.comboHoursModalShowed) {
        return Promise.resolve()
      }

      return modalOpen(MODALS.APPOINTMENT_COMBO_EDIT)
        .catch(() => {
          return Promise.reject()
        })
        .finally(() => {
          commit('setComboHoursModalShowed', true)
        })
    },

  },

  /*!
   *
   * mutations
   *
   */
  mutations: {

    setup (state, {
      appointment,
      appointmentPaymentDetails = null,
      model,
      customer,
      subbookingIndex = null,
      deliberatelyBookedTill = false,
    }) {
      state.appointment = appointment
      state.appointmentPaymentDetails = appointmentPaymentDetails
      state.model = model
      state.customer = customer
      state.drawer.subbookingIndex = subbookingIndex
      state.drawer.deliberatelyBookedTill = deliberatelyBookedTill
      state.drawer.comboHoursModalShowed = false
    },

    setValidationErrors (state, { key, value }) {
      state.hasErrors[key] = value
    },

    setModel (state, model) {
      state.model = model
    },

    setCustomer (state, customer) {
      state.customer = customer
    },

    setDrawerEditedIndex (state, { index, copy = true }) {
      if (copy) {
        state.drawer.copy = index !== null ? clone(state.model.subbookings[index]) : null
      }
      state.drawer.subbookingIndex = index
    },

    setModelValue (state, { name, value }) {
      state.model[name] = value
    },

    updateReservationData (state, updates) {
      for (const key in updates) {
        if (updates.hasOwnProperty(key)) {
          state.model.subbookings[0][key] = updates[key]
        }
      }
    },

    setComboHoursModalShowed (state, value) {
      state.drawer.comboHoursModalShowed = value
    },
  },

  /*!
   *
   * getters
   *
   */
  getters: {

    getSubbookingById: state => id => {
      const appointment = state.model || state.appointment
      return appointment.subbookings
        && appointment.subbookings.find(sb => sb.id === id)
    },

    getSubbookingByIndex: state => index => {
      return state.model.subbookings
        && state.model.subbookings[index]
    },

    getDeletedSubbookings: (state, getters) => {
      if (!getters.isEdited) return false

      const bookings = getters.getBookings
      if (!bookings) return {}

      if (bookings.length) {
        const appointment = state.appointment
        return appointment.subbookings.length > 1
          ? appointment.subbookings
            .reduce((ids, { id }) => {
              const isDeleted = bookings.findIndex(sb => sb.id === id) === -1
              if (id && isDeleted) ids[id] = true
              return ids
            }, {})
          : {}
      }
    },

    getBookings: ({ model, appointmentPaymentDetails, customer, drawer }, getters) => {
      if (!model) {
        return null
      }
      const bookingCustomer = clone(mapBookingCustomer(customer))

      return model.subbookings.reduce((bookings, sb, sbIndex) => {
        const multibooking = getters.isMultibooking
        let paid = false

        const eppo = useEppo()
        const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
        if (isPaymentDetailsFromBasketEnabled) {
          paid = !model.payable && appointmentPaymentDetails?.id && appointmentPaymentDetails?.basket_type === BASKET_TYPE.PAYMENT
        } else {
          const payment = model.payment_info
          paid = payment && !payment.payable && payment.transaction_info && payment.transaction_info.id && model && payment.transaction_info.status_code === 'P'
        }

        if (sb.combo_children?.length) {
          sb.combo_children.forEach((cc, ccIndex) => {
            const isNew = !cc.id && true
            const resources = resourcesInSubbooking({
              staffer_id: cc.staffer_id,
              appliance_id: cc.appliance_id,
            })

            const booking = {
              ...cc,
              id: `${sb.id || sbIndex}-${cc.id || ccIndex}`,
              ccId: cc.id,
              $meta: metaDataOfBooking({
                index: ccIndex,
                isNew,
                model,
                multibooking,
                customer: bookingCustomer,
                resources,
                // block: true,
                comboChildren: true,
                paid,
                parentId: sb.id,
                parentIndex: sbIndex,
              }),
            }

            bookings.push(booking)
          })
        } else {
          const isNew = !sb.id && true
          const resources = resourcesInSubbooking({
            staffer_id: sb.staffer_id,
            appliance_id: sb.appliance_id,
          })

          const booking = {
            ...sb,
            id: sb.id,
            $meta: metaDataOfBooking({
              index: sbIndex,
              isNew,
              model,
              multibooking,
              customer: bookingCustomer,
              resources,
              paid,
              block: getters.isMultibooking && drawer.subbookingIndex !== null && sbIndex !== drawer.subbookingIndex,
            }),
          }

          bookings.push(booking)
        }

        return bookings
      }, [])
    },

    getBookingsIdToReplace: ({ appointment, model }) => {
      if (!model) {
        return null
      }

      return appointment.subbookings.reduce((result, sb) => {
        if (sb.combo_children?.length) {
          sb.combo_children.map(cc => result.add(cc.id))
        }

        result.add(sb.id)
        return result
      }, new Set())
    },

    mode: ({ model }) => {
      return model && ((model.appointment_id && APPOINTMENT_MODES.EDIT) || APPOINTMENT_MODES.NEW)
    },

    isEdited: ({ model, appointment }, getters) => {
      if (!getters.mode) return false

      if (model.new_repeating) {
        return true
      }

      const propsList = ['booked_from', 'booked_till', 'customer', 'repeating', 'traveling', 'reason', 'business_note', 'business_secret_note']
      const _appointment = pick(propsList, appointment)
      const _model = pick(propsList, model)

      if (!equals(_appointment, _model)) {
        return true
      }

      if (model.subbookings.length !== appointment.subbookings.length) {
        return true
      }

      const subbokingProps = ['id', 'booked_from', 'booked_till', 'appliance_id', 'staffer_id', 'is_highlighted', 'service_variant', 'addons', 'combo_children', 'is_staffer_requested_by_client']
      const subbookingsCompare = model.subbookings.every((sb, index) => {
        const compareSb = pick(subbokingProps, appointment.subbookings[index] || {})
        return equals(compareSb, pick(subbokingProps, sb))
      })

      if (!subbookingsCompare) return true

      return false
    },

    subbookingsValidation: (state, getters) => {
      return state.model && state.model.subbookings.every(subbookingValidation)
    },

    isMultibooking ({ model }) {
      return model && model.subbookings.length > 1
    },

    customerHasContact: state => customerHasContact(state.customer),

    customerHasEmail: state => customerHasEmail(state.customer),

    customerHasCellPhone: state => customerHasCellPhone(state.customer),

    getCalendarCardId: state => {
      if (!state.model) return null

      const index = state.drawer.subbookingIndex || 0
      const id = state.model.subbookings[index].id || index
      return cardId(id)
    },

    anyValidationErrors: state => {
      const validationErrors = state.hasErrors
      return Object.keys(validationErrors).every((error) => validationErrors[error])
    },

    hasAnyComboError: state => (sbIndex) => {
      return state.model.subbookings[sbIndex]?.combo_children.some(hasStafferOrResourceAnyError)
    },

    hasAnySubbookingError: state => (sbIndex) => {
      return state.model.subbookings[sbIndex]
        && hasStafferOrResourceAnyError(state.model.subbookings[sbIndex])
    },

    isBooksyUser: state => {
      if (!state.customer) {
        return null
      }

      const { is_user, user, merged_data } = state.customer

      return !!(is_user || user || merged_data?.user)
    },

    isCreatingAppointment: state => state.model !== null && state.model.appointment_id === null,

    getPaymentRowByPaymentType: state => (paymentTypeCode) => {
      const eppo = useEppo()
      const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
      if (isPaymentDetailsFromBasketEnabled) {
        return state.appointmentPaymentDetails?.payments?.find(payment => [payment.payment_type.code, payment.payment_type.source].includes(paymentTypeCode))
      } else {
        const appointment = state.model || state.appointment
        return appointment.payment_info?.transaction_info?.payment_rows?.find(row => row.payment_type_code === paymentTypeCode)
      }
    },
  },
}

const _shouldShowBookingModeModal = (appointment, appointmentPaymentDetails, rootGetters, eppo, getters) => {
  const isStripePba = rootGetters['business/isPayByAppOnStripe']

  let isPrepayment = appointment.payment_info?.transaction_info?.payment_type_code === PAYMENT_TYPE.PREPAYMENT
  let isTransactionAuthorized = appointment.payment_info?.transaction_info?.short_status === RECEIPT_STATUS_TYPE.AUTHORIZED
  let isDepositInfoEmpty = !appointment.deposit_info

  const isManagerOrHigher = rootGetters['identity/isManagerOrHigher']
  const shouldStopShowBookingModeModal = (Number(localStorage.getItem(LOCAL_STORAGE_KEYS.SHOW_BOOKING_MODE_MODAL_COUNTER)) || 0) >= BOOKING_MODE_MODAL_MAX
  const isBusinessSemiAuto = rootGetters['business/isBusinessBookingSemiAuto']
  const isFeatureEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentBookingModeSplashScreen)

  const isPaymentDetailsFromBasketEnabled = eppo.ff(FEATURE_FLAGS.Feature_AppointmentPaymentDetailsFromBasket)
  if (isPaymentDetailsFromBasketEnabled) {
    isPrepayment = getters.getPaymentRowByPaymentType(BASKET_PAYMENT_TYPE_SOURCE.PREPAYMENT)
    isTransactionAuthorized = appointmentPaymentDetails?.payments_summary?.status?.code === BASKET_STATUS.PENDING
    isDepositInfoEmpty = appointmentPaymentDetails?.basket_type !== BASKET_TYPE.DEPOSIT
  }

  return isStripePba
    && isPrepayment
    && isTransactionAuthorized
    && isManagerOrHigher
    && !shouldStopShowBookingModeModal
    && isBusinessSemiAuto
    && isDepositInfoEmpty
    && isFeatureEnabled
}

const _shouldShowRefundModal = (paymentRow, rootGetters) => {
  const isRefundable = paymentRow?.refundable === true
  const isAdvancedOrHigher = rootGetters['identity/isAdvancedOrHigher']

  return !!paymentRow
    && isRefundable
    && isAdvancedOrHigher
}
